import { useContext, useEffect, useMemo, useState } from 'react';
import { isMonthOutsideEmployment, renderSpinner, prepareDaysColumnHeader } from '../../utils/Utility';
import { UserContext } from '../../context/UserContext';
import { Unauthorized } from '../../components/Unauthorized';
import { PERMISSIONS } from '../../constants';
import { MonthNavHeader } from '../../components/Layout/MonthNavHeader';
import { MonthContext } from '../../context/MonthContext';
import { MonoSelectUser } from '../../components/Form/MonoSelectUser';
import { EmployeeListContext } from '../../context/EmployeeListContext';
import AbsencesTable from './AbsencesTable';
import AbsencesYearlySummaryTable from './AbsencesYearlySummaryTable';
import { fetchAbsencesYearlySummary } from '../../utils/API/absencesYearlySummary';
import { fetchAbsences, deleteAbsence } from '../../utils/API/absences';
import I3Modal from '../../components/Layout/I3Modal';
import I3ModalConfirmation from '../../components/Layout/I3ModalConfirmation';
import { getReportDetail } from '../../utils/API/reports';
import AbsenceForm from './AbsenceForm';
import { useKeyPress } from '../../hooks/useKeyPress';
import { getCalendarWorklogsHeader } from '../../utils/API/calendar';

export const AbsencesPage = () => {
  const { user, hasClaim } = useContext(UserContext);
  const canSelectUser = hasClaim(PERMISSIONS.ABSENCES_USER_SELECTION);
  const isAuthorized = hasClaim(PERMISSIONS.ABSENCES_VIEW) || canSelectUser;
  const { month } = useContext(MonthContext);

  const { employeesQueryA: users, employeesByIdQueryA: usersById } =
    useContext(EmployeeListContext);

  const [impersonatedUserId, setImpersonatedUserId] = useState(user.id);
  const selectedUserData = useMemo(
    () => usersById[impersonatedUserId] || user,
    [usersById, impersonatedUserId, user]
  );
  const selectedUser = selectedUserData?.username;
  const selectedUserIsCurrentUser = useMemo(() => {
    if (!selectedUser) {
      return true;
    }
    return user?.username === selectedUser;
  }, [user, selectedUser]);

  const [absences, setAbsences] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dayHeaders, setDayHeaders] = useState([]);
  const [editFormData, setEditFormData] = useState();
  const [showModal, setShowModal] = useState(false);
  const [report, setReport] = useState({});
  const [yearlySummary, setYearlySummary] = useState(null);
  const [showModalDeleteConfirmOptions, setShowModalDeleteConfirmOptions] =
    useState({ show: false });
  const [isDeleteRunning, setIsDeleteRunning] = useState(false);
  // Chiave dell'ultimo riepilogo annuale caricato: dipende da utente E anno,
  // così cambiando utente selezionato il riepilogo viene comunque ricaricato.
  const [loadedSummaryKey, setLoadedSummaryKey] = useState(null);

  const loadData = (forceYearlySummary = false) => {
    setIsLoading(true);
    const year = month.format('YYYY');
    const summaryKey = `${selectedUser}|${year}`;
    const shouldLoadYearlySummary =
      forceYearlySummary || loadedSummaryKey !== summaryKey;

    getCalendarWorklogsHeader(month)
      .then((calendarWorklogsHeader) => {
        const res = prepareDaysColumnHeader({ month, calendarWorklogsHeader });
        setDayHeaders(res);
      })
      .then(() =>
        getReportDetail(year, month.format('MM'), selectedUser, 'worklogs')
      )
      .then((reportData) => setReport(reportData))
      .then(() => fetchAbsences(year, month.format('MM'), [selectedUser]))
      .then((res) => {
        setAbsences(res);
        // Chiamata riepilogo annuale solo se l'anno è cambiato o ci sono state modifiche
        if (shouldLoadYearlySummary) {
          setLoadedSummaryKey(summaryKey);
          return fetchAbsencesYearlySummary([selectedUser], year);
        }
        return yearlySummary;
      })
      .then((summary) => {
        if (shouldLoadYearlySummary) {
          setYearlySummary(summary);
        }
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (!isAuthorized || !selectedUser) return;
    loadData();
  }, [month, selectedUser, isAuthorized]);

  const isOutsideEmployment = isMonthOutsideEmployment(month, selectedUserData);

  const isReportConfirmed = report.confirmed || !report.id;
  // L'amministratore che consulta un altro utente vede la pagina in sola lettura.
  const isReadOnly = isReportConfirmed || !selectedUserIsCurrentUser;

  const handleToggleModal = () => setShowModal(!showModal);

  const handleHideDeleteConfirmModal = () => {
    setShowModalDeleteConfirmOptions({
      show: false,
    });
  };

  const handleDeleteConfirmModal = () => {
    setIsDeleteRunning(true);
    const { id } = showModalDeleteConfirmOptions;
    deleteAbsence(id)
      .then(() => {
        handleHideDeleteConfirmModal();
        loadData(true);
      })
      .finally(() => {
        setIsDeleteRunning(false);
      });
  };

  const handleAddClick = (date) => {
    if (isReadOnly || isOutsideEmployment) return;
    setEditFormData({
      id: undefined,
      selectedDate: date,
    });
    setShowModal(true);
  };

  const handleEditClick = (absence) => {
    setEditFormData(absence);
    setShowModal(true);
  };

  const handleDeleteClick = (id) => {
    setShowModalDeleteConfirmOptions({
      show: true,
      id,
    });
  };

  useKeyPress('F9', () => {
    if (isReadOnly) {
      return;
    }
    handleAddClick(undefined);
  });
  useKeyPress('Escape', () => {
    setShowModal(false);
  });

  if (!isAuthorized) {
    return <Unauthorized />;
  }

  return (
    <>
      <div className="content__wrapper">
        <MonthNavHeader
          title="Assenze"
          showInsertButton={!isReadOnly}
          isOutsideEmployment={isOutsideEmployment}
          onInsert={() => handleAddClick(undefined)}
        >
          {canSelectUser && (
            <MonoSelectUser select={setImpersonatedUserId} users={users} />
          )}
        </MonthNavHeader>

        <div className="content__overflow">
          {isLoading ? (
            renderSpinner(true)
          ) : (
            <div
              style={{ gap: '20px', display: 'flex', flexDirection: 'column' }}
            >
              {' '}
              <div
                className="tabsContainer"
                style={{ overflow: 'auto', gap: '20px' }}
              >
                <AbsencesTable
                  dayHeaders={dayHeaders}
                  data={absences}
                  isReadOnly={isReadOnly}
                  onAddClick={handleAddClick}
                  onEditClick={handleEditClick}
                  onDeleteClick={handleDeleteClick}
                />
              </div>
              <div
                className="tabsContainer"
                style={{ overflow: 'auto', gap: '20px' }}
              >
                <AbsencesYearlySummaryTable summary={yearlySummary} />
              </div>
            </div>
          )}
          <I3ModalConfirmation
            show={showModalDeleteConfirmOptions.show}
            onHide={handleHideDeleteConfirmModal}
            title="Elimina"
            questionText="Vuoi cancellare l'assenza selezionata?"
            onConfirm={handleDeleteConfirmModal}
            isLoading={isDeleteRunning}
          />

          <I3Modal
            show={showModal}
            onHide={handleToggleModal}
            title="Assenza"
            className="I3Modal-log-time"
          >
            <AbsenceForm
              month={month}
              close={handleToggleModal}
              editFormData={editFormData}
              onDataChanged={() => {
                loadData(true);
              }}
              isReadOnly={isReadOnly}
            />
          </I3Modal>
        </div>
      </div>
    </>
  );
};
